/**
 * event-description — the event read whole: name, dateline, poster,
 * description, venue and entry notes, mounted by `show()` when the header's
 * title, its clamped description, or the schedule affordance is pressed, and
 * by the landing archive when a card for an ended event is pressed. The same
 * facts `event-identity` renders inline for the header card, carried here at
 * reading scale rather than dropped: the header trims what a phone's first
 * screen has room for, and this overlay is where the buyer who wants the
 * whole picture gets it. An ended event has nothing to sell, so the archive
 * opens this reading in place of the buying page, which would land the buyer
 * on a shop with no purchasable ticket.
 *
 * The poster leads the body, at 20rem across a sheet that is at least 28rem
 * wide, so it reads as artwork rather than as a thumbnail — and it sits in
 * the same 2:3 box the card the buyer pressed already gave it, covered, so an
 * off-ratio poster crops here exactly as it cropped there. It is absent on
 * the buying page, whose header already carries the poster above this
 * overlay's own door.
 *
 * A named element and not an inline `show(() => html\`<div>…\`)`: an
 * anonymous root reports as the tag `div`, which names nothing, collides with
 * every other anonymous overlay on the page, and cannot anchor a live
 * assertion.
 *
 * The header — name, schedule, and the venue's directions link — is an
 * in-flow row (`flex items-start justify-between`), not a floating button
 * over a padded column: the close button sits as a sibling of the title
 * block at the same baseline, "eye level" with what it dismisses, matching
 * `legal-document`'s header shape. The button itself carries `size="sm"`
 * (40px) rather than the library's default `md` (48px): a dismissal is the
 * reading's most tertiary action, sized for a corner exit rather than a
 * focused-surface control. Dismissal still also comes from the overlay
 * itself — Escape, back-button, a tap outside — the button is a second,
 * discoverable door onto the same platform `close` event the overlay
 * already resolves its own subscription with.
 *
 * The header is `sticky top-0` with its own opaque surface background, so it
 * stays pinned above the description as the body scrolls — the directions
 * link travels with it, since a buyer orienting mid-read never loses the one
 * link back to the venue.
 *
 * The name is the headline the reading opens with, at `headline/sm` on the
 * default ink: half the openings arrive here because the name itself was cut
 * on the page, so the one thing the overlay is about is read at full ink and
 * one rung under the page title's own 32px ceiling — an overlay is subordinate
 * to the surface that opened it, and 32px inside a wide sheet would be louder
 * here than the title is there. The name and its schedule dateline share one
 * `gap-1` group rather than the reading's outer `gap-8` rhythm: "what" and
 * "when" are one fact read together, not two sibling sections, and outer-rung
 * spacing between them would read as a break the two halves of one line don't
 * have. The venue and notes lines carry the exact typography `event-identity`
 * gives them in the header — `body/sm` for the where and the entry notes — so
 * the fuller reading and the header's own summary never disagree about which
 * rung a fact belongs on; they also sit in their own group below the
 * description, set off by the outer `gap-8` rather than the `gap-4` separating
 * one paragraph from the next, because they are reference facts a buyer
 * checks after reading, not more of the reading itself.
 *
 * The entry notes carry `role="note"` alongside their existing aria-label: a
 * generic element's `aria-label` has no accessible-name computation to anchor
 * to without an ARIA role, so without it a screen reader announces the notes
 * as an undifferentiated continuation of the description rather than their
 * own labeled region. `note`, not `group` — the notes are a passage of
 * ancillary text, not a set of interactive controls, and `group` reads to a
 * screen reader as the latter.
 *
 * The body is `body/lg` at `leading-relaxed`. 14px is the size the description takes
 * in the header, where it competes with the ticket types for a column; this
 * overlay exists because the reader chose to read, and 16px is where sustained
 * reading starts.
 *
 * The author's own structure — a title, date lines, an address block — is
 * carried two ways rather than one. Every single newline stays a hard break
 * inside its paragraph (`whitespace-pre-line`), and every blank line becomes a
 * real 16px interval: `whitespace-pre-line` alone renders a blank line as
 * exactly one empty line box, so a paragraph break measures the same as the
 * line pitch inside a paragraph at every leading, a 1:1 ratio no line-height
 * can move. Splitting on the blank line and stacking the parts in a grid gives
 * the break a designed interval — 26px baseline to baseline inside a paragraph
 * (16 × 1.625) against 42px across one, which is φ to within 0.16%, satisfied
 * rather than imposed: both numbers were picked for other reasons and landed
 * on it.
 *
 * The measure is declared on the HOST —
 * `min(clamp(28rem, 60vw, 44rem), 100dvw - 2rem)` — because the host is the
 * box whose measure the overlay actually reads. Both doors call `show()` from
 * inside a `@click`, so the ambient gesture anchor resolves the layout to
 * `anchored`, whose surface is `w-max`: a track sized from its one item's own
 * max-content contribution. A percentage anywhere INSIDE this element
 * therefore resolves against a track this element itself just widened and
 * clamps nothing — a `w-full` root reproduces the content's own max-content,
 * so a short description renders as a narrow pill, and so does an event whose
 * description is null, where the name is the whole content. 28rem (still
 * `ticket-type-description`'s flat cap) is the floor this reading never
 * shrinks below on a real device; 44rem is the ceiling it grows to on a wide
 * viewport, where the extra measure still reads as one comfortable text
 * column rather than a sheet stretched thin; `60vw` is what carries the
 * surface between the two, so a tablet or desktop opening earns real screen
 * width instead of sitting at a phone-sized column for no reason. The outer
 * `min(...)` against `100dvw - 2rem` is the anchored surface's own cap
 * restated, so on a real phone the reading is still exactly the viewport less
 * its inset — `60vw` on a phone-sized viewport already reads under 28rem, so
 * the clamp never fires there and the floor behavior is unchanged. It is the
 * one `:host` rule the overlay contract admits, because it declares a measure
 * and claims no surface: no `display`, no background, no height cap, no
 * scroll region. The host owns the measure alone, so the body carries no
 * `max-w-prose`: at 16px it resolves near 39rem, inside the 44rem ceiling —
 * adding it would stop the reading column from growing before the host does,
 * recreating the same narrow-column complaint this responsive width exists to
 * fix, just at a different breakpoint.
 *
 * `max-width: 100%` rides beside it, and today it binds nowhere: the overlay's
 * mount declares its rows and leaves its column implicit, so that column is
 * `auto` — sized from this element's own max-content contribution, which the
 * `width` above just fixed — and the percentage resolves against a track this
 * element itself sized. It costs nothing on any real surface: the storefront
 * declares no `[data-fill-boundary]` and both doors open from a `@click`, so
 * every real opening is anchored, and on a real phone `100dvw - 2rem` IS the
 * frame. It costs the studio, where a 375px frame drawn on a desktop reads
 * `dvw` as the desktop and lays this element out at 448px with its right edge
 * clipped — measured. A `minmax(0, 1fr)` column on the overlay's own mount is
 * what makes the percentage bind, and that mount is schmancy's.
 *
 * Untruncated at any length, exactly as `ticket-type-row` promises for a
 * type's description: `Event.EventSchema` caps nothing, so the overlay scrolls
 * rather than trims. It declares no height cap and owns no scroll region: the
 * overlay's own mount is already `overflow-auto` under a surface capped at 90%
 * of whatever is the viewport there — the window, or the frame a
 * `[data-fill-boundary]` declares. A `max-h-[80vh]` here would measure the real
 * window instead, so inside a 375px phone frame drawn on a desktop it resolves
 * larger than the frame and the sheet spills out of the phone it is supposed to
 * be inside. One owner for the scrollport, and it is the one that knows which
 * box is the viewport.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { Event } from "@momo/types";
import { css, html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

@schmancyElement("event-description")
export class EventDescriptionView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				width: min(clamp(28rem, 60vw, 44rem), 100dvw - 2rem);
				max-width: 100%;
			}
		`,
	];

	/** The event's name — the overlay's headline, so the full text is never
	 *  read detached from what it describes, and the name a shortened title
	 *  withheld is read here. */
	@property({ type: String }) name = "";
	@property({ type: String }) description = "";
	/** The run, already read in the venue's own zone by whoever opened this —
	 *  never derived a second time here. Each caller already renders that
	 *  dateline on the surface the buyer pressed (the header's own line, the
	 *  archived card's label), and a second derivation inside this overlay is
	 *  a second expression of one fact, free to disagree with the first over
	 *  locale or month form. */
	@property({ type: String }) dateLabel = "";
	/** The event's artwork, at reading scale — absent on the buying page,
	 *  whose header already shows the poster above this overlay's own door,
	 *  and present from the archive, where the poster is what the buyer opened
	 *  the card for. */
	@property({ type: String }) poster: string | null = null;
	@property({ type: String }) venue: string | null = null;
	/** Absent when the venue resolves to no pin and no labeled address; the
	 *  locator then reads as plain text rather than a link to nowhere. */
	@property({ type: String }) mapsUrl: string | null = null;
	/** Entrance instructions — the same fact `event-identity` gives the header,
	 *  carried here so the fuller reading never drops one of its facts. */
	@property({ type: String }) notes: string | null = null;

	override render(): TemplateResult {
		return html`
			<div class="flex flex-col gap-6">
				<div class="sticky top-0 z-10 flex items-start justify-between gap-4 bg-surface-default px-6 pt-6 pb-2">
					<div class="flex min-w-0 flex-col gap-1">
						<schmancy-typography type="headline" token="sm" class="min-w-0 break-words">${this.name}</schmancy-typography>

						<schmancy-typography
							type="label"
							token="sm"
							transform="uppercase"
							class="tracking-widest text-surface-onVariant"
						>
							${this.dateLabel}
						</schmancy-typography>

						${when(this.venue, () =>
							this.mapsUrl
								? html`
										<a
											href=${this.mapsUrl}
											target="_blank"
											rel="noopener"
											aria-label=${this.t("storefront.eventDescription.venueMapAriaLabel", { venue: this.venue ?? "" })}
											class="flex w-fit min-w-0 max-w-full cursor-pointer items-center gap-1"
										>
											<schmancy-icon class="text-sm">directions</schmancy-icon>
											<schmancy-typography type="body" token="sm" class="min-w-0 break-words">${this.venue}</schmancy-typography>
										</a>
									`
								: html`<schmancy-typography type="body" token="sm" class="min-w-0 break-words">${this.venue}</schmancy-typography>`,
						)}
					</div>

					<schmancy-icon-button
						icon="close"
						size="sm"
						variant="text"
						aria-label=${this.t("storefront.eventDescription.closeAriaLabel")}
						@click=${() => {
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							);
						}}
					></schmancy-icon-button>
				</div>

				<div class="flex flex-col gap-8 px-6 pb-6">
					${when(
						this.poster,
						() => html`
							<img
								src=${this.poster ?? ""}
								alt=""
								width=${Event.POSTER_WIDTH}
								height=${Event.POSTER_HEIGHT}
								decoding="async"
								class="aspect-[2/3] w-full max-w-xs rounded-xl object-cover"
							/>
						`,
					)}

					${when(
						this.description,
						() => html`
							<div class="grid gap-4">
								${repeat(
									this.description.split(/\n{2,}/),
									(_paragraph, index) => index,
									(paragraph) => html`
										<schmancy-typography
											type="body"
											token="lg"
											class="min-w-0 whitespace-pre-line break-words leading-relaxed"
											>${paragraph}</schmancy-typography
										>
									`,
								)}
							</div>
						`,
					)}

					${when(
						this.notes,
						() => html`
							<schmancy-typography
								type="body"
								token="sm"
								role="note"
								class="min-w-0 break-words text-surface-onVariant"
								aria-label=${this.t("storefront.eventDescription.entryInfoAriaLabel")}
							>
								${this.notes}
							</schmancy-typography>
						`,
					)}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-description": EventDescriptionView;
	}
}
